import { get, getAll, meta, put } from "../db/idb.ts";
import * as repo from "../db/repo.ts";
import type {
  BathingGroup, Comment, FuelLoad, HeatSession, Measurement, Position, Rating, Reading,
  StoveEvent,
} from "../types.generated.ts";

/**
 * The demo's render functions are kept as they are — they were tuned against
 * real use and there is nothing to gain by rewriting them. This maps between
 * their view model and the rows the schema actually stores, in both
 * directions, so the screens never learn where the data came from.
 */

export interface VRound { id: string; at: number; by: string; vals: Record<string, number> }
/**
 * `id` is not decoration. The screens call endGroup(g.id) to close a group,
 * and this projection used to drop the id — so every group rebuilt from the
 * store after a reload had none, and ending it silently did nothing. The
 * evening then stayed open for ever, which is the state this field prevents.
 */
export interface VGroup { id: string; n: number; start: number; end: number | null;
                          people: number | null }
export interface VSession {
  id: string; date: string; status: string;
  fireStart: number; burnEnd: number | null;
  bathingStart: number | null; bathingEnd: number | null;
  fuelLoads: number[]; groups: VGroup[];
  outdoor: number | null; windDeg: number | null; windMs: number | null;
  wood: string | null; autoClosed: boolean; backfilled: boolean;
  rounds: VRound[];
  ratings: { u: string; s: number }[];
  comments: { u: string; at: number; body: string }[];
}
export interface VPosition {
  id: string; code: string; fi: string; en: string;
  c: number; r: number; retired: boolean; slot: number;
}

const ms = (iso: string | null | undefined): number | null => (iso ? Date.parse(iso) : null);
const isoDate = (t: number) => {
  const d = new Date(t);
  const p = (n: number) => (n < 10 ? "0" : "") + n;
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** The seven points a new stove starts with, if the server has none yet. */
export const DEFAULT_LAYOUT = [
  { code: "YV", fi: "Ylä vasen", en: "Top left", c: 1, r: 1 },
  { code: "YO", fi: "Ylä oikea", en: "Top right", c: 3, r: 1 },
  { code: "KV", fi: "Keski vasen", en: "Middle left", c: 1, r: 2 },
  { code: "KK", fi: "Keski", en: "Middle", c: 2, r: 2 },
  { code: "KO", fi: "Keski oikea", en: "Middle right", c: 3, r: 2 },
  { code: "AV", fi: "Ala vasen", en: "Bottom left", c: 1, r: 3 },
  { code: "AO", fi: "Ala oikea", en: "Bottom right", c: 3, r: 3 },
];

/**
 * Which sauna this device is actually working with. A device that has
 * synced uses the server's; one that never has uses the one it made itself.
 */
export async function currentSaunaId(fallback: string): Promise<string> {
  return (await meta<string>("sauna_id")) ?? fallback;
}

/** The stove belonging to that sauna — the local one until a sync says otherwise. */
export async function stoveOf(sauna_id: string): Promise<string | null> {
  const s = (await getAll<{ id: string; sauna_id: string }>("stove"))
    .find((x) => x.sauna_id === sauna_id);
  return s?.id ?? null;
}

export async function loadPositions(stove_id: string): Promise<VPosition[]> {
  const rows = (await getAll<Position>("position")).filter((p) => p.stove_id === stove_id);
  return rows
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p, i) => ({
      id: p.id, code: p.code, fi: p.label_fi, en: p.label_en,
      c: p.grid_col, r: p.grid_row, retired: !!p.retired_at, slot: i,
    }));
}

/** Everything the archive and the live screen read, in one pass. */
/** The owner's own starting targets, held on the position rows (FR-7.8a). */
export async function saunaName(sauna_id: string): Promise<string> {
  const s = await get<{ name?: string }>("sauna", sauna_id);
  return s?.name ?? "Savusauna";
}

/**
 * This account's role in this sauna, from the membership rows the sync
 * brought down. A device that has never pulled has no membership row at all
 * — and the only way to be in that state is to have made the sauna here,
 * which the server's trigger makes you the owner of. Saying OWNER there is
 * what keeps the invite button from disappearing on the very device that
 * needs it; every call it enables is checked again by `is_owner()` on the
 * server, so a wrong guess costs a clear error, not access.
 */
export async function myRole(
  sauna_id: string, user_id: string | null,
): Promise<"OWNER" | "MEMBER" | null> {
  if (!user_id) return null;
  const rows = await getAll<{ sauna_id: string; user_id: string; role: string;
                              removed_at: string | null }>("membership");
  const here = rows.filter((r) => r.sauna_id === sauna_id && !r.removed_at);
  const mine = here.find((r) => r.user_id === user_id);
  if (mine) return mine.role === "OWNER" ? "OWNER" : "MEMBER";
  return here.length ? null : "OWNER";
}

export async function saunaLocation(sauna_id: string):
  Promise<{ lat: number | null; lon: number | null; label: string | null }> {
  const s = await get<{ location_lat?: number | null; location_lon?: number | null;
                        location_label?: string | null }>("sauna", sauna_id);
  return { lat: s?.location_lat ?? null, lon: s?.location_lon ?? null,
           label: s?.location_label ?? null };
}

export async function loadSeedTargets(stove_id: string): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  for (const p of await getAll<Position>("position")) {
    if (p.stove_id === stove_id && typeof p.seed_target_c === "number") {
      out[p.id] = p.seed_target_c;
    }
  }
  return out;
}

export async function loadStoveEvents(stove_id: string) {
  return (await getAll<StoveEvent>("stove_event"))
    .filter((e) => e.stove_id === stove_id)
    .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
    .map((e) => ({ id: e.id, at: Date.parse(e.occurred_at), type: e.type, note: e.note ?? "" }));
}

/**
 * The screens carry a position as {id, code, fi, en, c, r, retired, slot};
 * the database carries the same point with its own column names and a seed
 * target. This is the one place that knows both.
 */
export async function savePositionFromView(
  vp: VPosition, stove_id: string, seed: number | null,
): Promise<void> {
  const existing = await get<Position>("position", vp.id);
  await repo.savePosition({
    ...(existing ?? {}),
    id: vp.id, stove_id, code: vp.code, label_fi: vp.fi, label_en: vp.en,
    grid_col: vp.c, grid_row: vp.r, sort_order: vp.slot,
    seed_target_c: seed,
    retired_at: vp.retired ? (existing?.retired_at ?? new Date().toISOString()) : null,
    created_at: existing?.created_at ?? new Date().toISOString(),
  } as Position);
}

export async function loadSessions(sauna_id: string): Promise<VSession[]> {
  const [sessions, measurements, readings, groups, fuel, ratings, comments] = await Promise.all([
    getAll<HeatSession>("heat_session"), getAll<Measurement>("measurement"),
    getAll<Reading>("reading"), getAll<BathingGroup>("bathing_group"),
    getAll<FuelLoad>("fuel_load"), getAll<Rating>("rating"), getAll<Comment>("comment"),
  ]);
  const byMeasurement = new Map<string, Reading[]>();
  for (const r of readings) {
    const list = byMeasurement.get(r.measurement_id) ?? [];
    list.push(r); byMeasurement.set(r.measurement_id, list);
  }

  return sessions
    .filter((s) => s.sauna_id === sauna_id && !s.deleted_at)
    .map((s): VSession => {
      const fire = ms(s.fire_start_at) ?? ms(s.created_at) ?? Date.now();
      return {
        id: s.id,
        date: isoDate(fire),
        status: s.status,
        fireStart: fire,
        burnEnd: ms(s.firebox_cleared_at),
        bathingStart: ms(s.bathing_start_at),
        bathingEnd: ms(s.bathing_end_at),
        outdoor: s.outdoor_temp_c ?? null,
        windDeg: s.wind_dir_deg ?? null,
        windMs: s.wind_speed_ms ?? null,
        wood: s.wood_species ?? null,
        autoClosed: s.auto_closed,
        backfilled: s.backfilled,
        fuelLoads: fuel.filter((f) => f.session_id === s.id)
          .map((f) => Date.parse(f.loaded_at)).sort((a, b) => a - b),
        groups: groups.filter((g) => g.session_id === s.id)
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((g) => ({ id: g.id, n: g.ordinal, start: Date.parse(g.started_at),
                         end: ms(g.ended_at), people: g.person_count ?? null })),
        rounds: measurements.filter((m) => m.session_id === s.id)
          .sort((a, b) => a.measured_at.localeCompare(b.measured_at))
          .map((m) => {
            const vals: Record<string, number> = {};
            for (const r of byMeasurement.get(m.id) ?? []) {
              if (!r.skipped && r.temperature_c != null) vals[r.position_id] = r.temperature_c;
            }
            return { id: m.id, at: Date.parse(m.measured_at), by: m.recorded_by ?? "", vals };
          }),
        ratings: ratings.filter((r) => r.session_id === s.id)
          .map((r) => ({ u: r.author_name, s: r.score })),
        comments: comments.filter((c) => c.session_id === s.id && !c.deleted_at)
          .sort((a, b) => a.created_at.localeCompare(b.created_at))
          .map((c) => ({ u: c.author_name, at: Date.parse(c.created_at), body: c.body })),
      };
    })
    .sort((a, b) => a.fireStart - b.fireStart);
}

/**
 * A first run with nothing on the server: create the sauna, the stove and
 * the default seven points locally so the app is usable before it has ever
 * reached a network. They carry their final ids from the start, so the sync
 * that follows is an insert, not a reconciliation.
 */
export async function ensureLocalSauna(name: string): Promise<{ sauna_id: string; stove_id: string }> {
  const existing = await getAll<{ id: string }>("sauna");
  if (existing[0]) {
    const stove = (await getAll<{ id: string; sauna_id: string }>("stove"))
      .find((s) => s.sauna_id === existing[0]!.id);
    if (stove) return { sauna_id: existing[0].id, stove_id: stove.id };
  }
  const sauna_id = crypto.randomUUID();
  const stove_id = crypto.randomUUID();
  await put("sauna", { id: sauna_id, name, timezone: "Europe/Helsinki",
                       created_at: new Date().toISOString() } as never);
  await put("stove", { id: stove_id, sauna_id, name: "Kiuas",
                       created_at: new Date().toISOString() } as never);
  let i = 0;
  for (const p of DEFAULT_LAYOUT) {
    await put("position", {
      id: crypto.randomUUID(), stove_id, code: p.code, label_fi: p.fi, label_en: p.en,
      grid_col: p.c, grid_row: p.r, sort_order: i++, seed_target_c: null,
      retired_at: null, created_at: new Date().toISOString(),
    } as never);
  }
  return { sauna_id, stove_id };
}

// ---- writes: every one of these goes through the repo, never straight to IDB ----

export const commit = {
  startSession: repo.startSession,
  patchSession: repo.patchSession,
  deleteSession: repo.deleteSession,
  recordRound: repo.recordRound,
  updateRound: repo.updateRound,
  startGroup: repo.startGroup,
  endGroup: repo.endGroup,
  addFuelLoad: repo.addFuelLoad,
  rate: repo.rate,
  addComment: repo.addComment,
  savePosition: savePositionFromView,
  addStoveEvent: repo.addStoveEvent,
  setSaunaLocation: repo.setSaunaLocation,
  setSaunaName: repo.setSaunaName,
  adoptLocalRows: repo.adoptLocalRows,
};
